/**
 * TNCY-9 §d — `useOwnerMemberDataAcquisitions` merged owner/member list for
 * the Data Acquisitions page (lyse app).
 *
 * Mirrors the apps/web Contracts `useOwnerMemberContracts` (TNCY-6b) but for
 * the lyse DA list, which the BE extended with `scope=member` + ownership
 * metadata (`ownership`/`ownerTenantName`/`role`) on the `DataAcquisition`
 * list row in lyse/v1@8.1.0 (TNCY-8). Member rows are DAs of member projects
 * the caller was granted cross-tenant eval access to (`project_contractor` /
 * `reporting_subcontractor` / `contract_contractor`, XT-D22), resolved via the
 * DA → inspection_instruction → project chain. The BE already projects them
 * to the caller's role surface and omits out-of-surface rows (a customer sees
 * none, §e / AC-3), so the FE just renders what it gets.
 *
 * It fires BOTH scopes and merges them into one table via the shared
 * `mergeOwnerMember` primitive (`@aeriosiq/ownership`):
 *
 *   - `scope=own`     — the caller's own DAs, SERVER-paginated.
 *   - `scope=member`  — the cross-tenant fan-out, loaded in FULL but BOUNDED
 *                       (one page at `MEMBER_PAGE_SIZE`).
 *
 * Own-only fast path: when there are no member rows the hook returns the own
 * query's items + its server `total` verbatim, so a tenant with no
 * cross-tenant grants sees exactly the pre-TNCY-9 list.
 *
 * Client-sort note: the BE returns each scope already sorted by the active
 * `sort_by`/`sort_order`. We re-sort the merged union by the fields present
 * on the DA row (instruction code/date, state, severity, and the enriched
 * `assetIdentifier`/`siteName` name fields). The name-only server sort keys
 * that depend on cross-DB enrichment the FE doesn't hold at merge time
 * (`project_name`/`pilot_name`/`turbine_type`/`assignee`) fall back to the
 * stable `instructionDate` proxy — own rows stay paginated and members are an
 * additive bounded overlay (OQ-TNCY-6).
 */
import { useMemo } from "react";
import { useApi } from "@aeriosiq/api-client";
import { useQuery } from "@tanstack/react-query";
import { mergeOwnerMember, MEMBER_PAGE_SIZE } from "@aeriosiq/ownership";
import type {
  DataAcquisition,
  DataAcquisitionSortKey,
  DataAcquisitionSortOrder,
  GetDataAcquisitionsParams,
  GetDataAcquisitionsResponse,
} from "@aeriosiq/types";
import { dataAcquisitionKeys, useDataAcquisitionsQuery } from "./queries.js";

export interface OwnerMemberDataAcquisitionsResult {
  rows: DataAcquisition[];
  total: number;
  hasMemberRows: boolean;
  memberTruncated: boolean;
  isPending: boolean;
  isError: boolean;
  /** Surfaced so the page can keep its existing error card. */
  error: unknown;
  refetch: () => void;
}

function compareBy(
  key: DataAcquisitionSortKey | undefined,
  order: DataAcquisitionSortOrder | undefined,
): (a: DataAcquisition, b: DataAcquisition) => number {
  const sign = order === "asc" ? 1 : -1;
  return (a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case "instruction_code":
        av = a.instructionCode;
        bv = b.instructionCode;
        break;
      case "state":
        av = a.state;
        bv = b.state;
        break;
      case "max_severity":
        av = a.maxSeverity ?? -1;
        bv = b.maxSeverity ?? -1;
        break;
      case "asset_identifier":
        av = a.assetIdentifier ?? "";
        bv = b.assetIdentifier ?? "";
        break;
      case "site_name":
        av = a.siteName ?? "";
        bv = b.siteName ?? "";
        break;
      // instruction/planned/flight dates AND the name-only enriched sorts the
      // FE can't resolve at merge time (project_name / pilot_name /
      // turbine_type / assignee) all fall back to the stable instruction-date
      // proxy — own rows stay paginated, members are a bounded overlay.
      default:
        av = a.instructionDate;
        bv = b.instructionDate;
    }
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv)) * sign;
  };
}

/**
 * @param params  the page's query params (page/pageSize/sort/filters). The
 *                hook derives the own + member queries from it; do NOT pass
 *                `scope` — it's set internally.
 */
export function useOwnerMemberDataAcquisitions(
  params: GetDataAcquisitionsParams,
): OwnerMemberDataAcquisitionsResult {
  const api = useApi();

  const ownParams = useMemo<GetDataAcquisitionsParams>(
    () => ({ ...params, scope: "own" }),
    [params],
  );
  const own = useDataAcquisitionsQuery(ownParams);

  // MEMBER — same filters, bounded single page; sort handled client-side on
  // the merged union. Best-effort overlay: keep this query off the page's
  // error/pending spine.
  const memberParams = useMemo<GetDataAcquisitionsParams>(
    () => ({
      ...params,
      scope: "member",
      page: 1,
      pageSize: MEMBER_PAGE_SIZE,
    }),
    [params],
  );
  const member = useQuery<GetDataAcquisitionsResponse>({
    queryKey: dataAcquisitionKeys.list(memberParams),
    queryFn: async () => {
      const response = await api.get<GetDataAcquisitionsResponse>(
        "/data-acquisitions",
        {
          params: toMemberQueryParams(memberParams),
          paramsSerializer: { indexes: null },
        },
      );
      return response.data;
    },
    placeholderData: (prev) => prev,
    retry: false,
  });

  const sortKey = params.sort_by?.[0];
  const sortOrder = params.sort_order?.[0];

  return useMemo<OwnerMemberDataAcquisitionsResult>(() => {
    const memberItems = member.data?.items ?? [];
    const merged = mergeOwnerMember<DataAcquisition>({
      ownItems: own.data?.items ?? [],
      ownTotal: own.data?.total ?? 0,
      memberItems,
      memberTotal: member.data?.total ?? memberItems.length,
      getId: (r) => r.id,
      compare: compareBy(sortKey, sortOrder),
    });
    return {
      ...merged,
      isPending: own.isPending,
      isError: own.isError,
      error: own.error,
      refetch: () => {
        void own.refetch();
      },
    };
  }, [
    own.data,
    own.isPending,
    own.isError,
    own.error,
    own.refetch,
    member.data,
    sortKey,
    sortOrder,
  ]);
}

/** Mirror of `queries.ts#toQueryParams` for the member overlay, with `scope`
 *  always set. Kept local so the own query's serializer stays the canonical
 *  one. */
function toMemberQueryParams(p: GetDataAcquisitionsParams) {
  const out: Record<string, unknown> = {};
  if (p.page !== undefined) out.page = p.page;
  if (p.pageSize !== undefined) out.pageSize = p.pageSize;
  if (p.q) out.q = p.q;
  if (p.state && p.state.length > 0) out.state = p.state;
  if (p.include_completed) out.include_completed = true;
  if (p.project_id && p.project_id.length > 0) out.project_id = p.project_id;
  if (p.site_id && p.site_id.length > 0) out.site_id = p.site_id;
  if (p.assignee_id && p.assignee_id.length > 0)
    out.assignee_id = p.assignee_id;
  if (p.date_from) out.date_from = p.date_from;
  if (p.date_to) out.date_to = p.date_to;
  if (p.sort_by && p.sort_by.length > 0) out.sort_by = p.sort_by;
  if (p.sort_order && p.sort_order.length > 0) out.sort_order = p.sort_order;
  out.scope = "member";
  return out;
}
